/* ~/assets/half-product-media-slider.js – V7 */
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

if (!customElements.get('half-product-media-slider')) {
  class HalfProductMediaSlider extends HTMLElement {
    constructor() {
      super();
      this.init = false;
      this.onResize = this.onResize.bind(this);
      this.onMqlChange = this.onMqlChange.bind(this);
    }

    connectedCallback() {
      if (this.init) return;
      this.init = true;

      /* ---- Selectors ---- */
      this.dtTracks = Array.from(this.querySelectorAll('.hpms-track'));
      this.mbTrack = this.querySelector('.hpms-mb-track');

      this.btnPrev = this.querySelector('[data-prev]');
      this.btnNext = this.querySelector('[data-next]');
      this.dots = Array.from(this.querySelectorAll('.hpms-dot'));

      /* ---- State ---- */
      this.index = 0;
      this.total = parseInt(this.getAttribute('data-total-slides') || '0', 10);
      this.mbSlides = this.mbTrack
        ? Array.from(this.mbTrack.querySelectorAll('.hpms-mb-slide'))
        : [];

      /* ---- Desktop breakpoint (must match CSS) ---- */
      this.desktopMql = window.matchMedia('(min-width: 750px)');

      this.bindEvents();

      if (this.mbTrack && this.mbSlides.length > 0) {
        this.initObserver();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.desktopMql?.removeEventListener('change', this.onMqlChange);
      if (this.observer) this.observer.disconnect();
    }

    onMqlChange(e) {
      if (e.matches) {
        this.syncDesktopTracks();
      } else {
        this.scrollToMobileSlide(this.index);
      }
    }

    /* ==================================================
       Events
       ================================================== */
    bindEvents() {
      /* Desktop nav */
      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', (e) => {
          e.preventDefault();
          this.move(-1);
        });
      }
      if (this.btnNext) {
        this.btnNext.addEventListener('click', (e) => {
          e.preventDefault();
          this.move(1);
        });
      }

      /* Mobile dots */
      this.dots.forEach((dot, idx) => {
        dot.addEventListener('click', () => this.scrollToMobileSlide(idx));
      });

      /* Resize — sync index across layouts */
      window.addEventListener('resize', this.onResize, { passive: true });

      /* FIX #3: Sync tracks when crossing the 750 px breakpoint */
      this.desktopMql.addEventListener('change', this.onMqlChange);

      /* Quick-add delegation */
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn || !this.contains(btn)) return;
        this.addToCart(e, btn);
      });
    }

    /* ==================================================
       Mobile – IntersectionObserver swipe tracking
       ================================================== */
    initObserver() {
      const options = {
        root: this.mbTrack,
        threshold: 0.5,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const slideIndex = this.mbSlides.indexOf(entry.target);
            if (slideIndex !== -1) {
              this.index = slideIndex;
              this.updateDots(slideIndex);
            }
          }
        });
      }, options);

      this.mbSlides.forEach((slide) => this.observer.observe(slide));
    }

    updateDots(activeIndex) {
      this.dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === activeIndex);
      });
    }

    scrollToMobileSlide(idx) {
      if (!this.mbTrack || !this.mbSlides.length) return;
      const slide = this.mbSlides[idx];
      if (!slide) return;

      this.mbTrack.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth',
      });
    }

    /* ==================================================
       Desktop slider
       ================================================== */
    move(dir) {
      if (this.total <= 1) return;
      this.index = (this.index + dir + this.total) % this.total;
      this.syncDesktopTracks();
    }

    /** Push all desktop tracks to the current this.index position */
    syncDesktopTracks() {
      this.dtTracks.forEach((track) => {
        track.style.transform = `translateX(-${this.index * 100}%)`;
      });
    }

    /* ==================================================
       Resize
       ================================================== */
    onResize() {
      if (this.mbTrack) {
        this.mbSlides = Array.from(this.mbTrack.querySelectorAll('.hpms-mb-slide'));
      }
    }

    /* ==================================================
       Horizon Quick-Add
       ================================================== */
    getCartCount(sections) {
      if (!sections) return null;
      for (const html of Object.values(sections)) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.querySelector('[ref="cartItemCount"]');
        if (el) {
          const n = parseInt(el.textContent.trim(), 10);
          return isNaN(n) ? null : n;
        }
      }
      return null;
    }

    flyToCart(btn) {
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      if (!cartIcon || !customElements.get('fly-to-cart')) return;
      const slide = btn.closest('.hpms-slide, .hpms-mb-slide');
      const img = slide?.querySelector('.hpms-img-contain, .hpms-img-cover');
      const imgUrl = img?.getAttribute('src');
      if (!imgUrl) return;
      const el = document.createElement('fly-to-cart');
      el.classList.add('fly-to-cart--main');
      el.style.setProperty('background-image', `url(${imgUrl})`);
      el.style.setProperty('--start-opacity', '0');
      el.source = btn;
      el.destination = cartIcon;
      document.body.appendChild(el);
    }

    animateAdded(btn) {
      btn.setAttribute('data-added', 'true');
      setTimeout(() => btn.removeAttribute('data-added'), 900);
    }

    async addToCart(e, btn) {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      this.flyToCart(btn);
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = [];
        cartItemsComponents.forEach((item) => {
          if (item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
        });
        if (sectionIds.length) formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);

        const config = fetchConfig('javascript');
        config.body = formData;
        delete config.headers['Content-Type'];

        const route = window.Theme?.routes?.cart_add_url || '/cart/add';
        const response = await fetch(route, config);
        const data = await response.json();

        if (data.status) {
          this.dispatchEvent(
            new CartErrorEvent(variantId, data.message, data.description, data.errors)
          );
          return;
        }

        /* Success */
        this.animateAdded(btn);
        this.dispatchEvent(
          new CartAddEvent({}, variantId, {
            source: 'half-product-media-slider',
            itemCount: this.getCartCount(data.sections),
            productId: btn.dataset.productId,
            sections: data.sections,
            variantId: variantId,
          })
        );
      } catch (err) {
        console.error('[hpms] add-to-cart failed:', err);
        this.dispatchEvent(
          new CartErrorEvent(variantId, 'Error', 'Failed to add to cart.', {})
        );
      } finally {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('half-product-media-slider', HalfProductMediaSlider);
}